import { JsxElement, JsxElementType } from "@/utils/jsxElementTypes";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { setDraggingComponent } from "@/store/iframeSlice";
import { AssetsElement, useImagePreview } from "@/hooks/useImagePreview";
import React, { useCallback } from "react";
import useDesignSystemStore, {
  GenerationStatus,
} from "@/store/zustand/aiDesignSystemBuilder";
import {
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "../ui/context-menu";
import { ContextMenu } from "../ui/context-menu";
import { MagicWandIcon } from "@radix-ui/react-icons";
import { BaseCard } from "../Community/CommunityCard";

interface AssetContextMenuProps {
  children: React.ReactNode;
  fullPath: string;
}

const AssetContextMenu = React.memo(
  ({ children, fullPath }: AssetContextMenuProps) => {
    const status = useDesignSystemStore((state) => state.generation.status);
    const addFile = useDesignSystemStore((state) => state.addFile);
    const resetGeneration = useDesignSystemStore(
      (state) => state.resetGeneration
    );

    const onAddToDesignSystem = useCallback(
      ({ reset }: { reset: boolean }) => {
        if (reset) {
          resetGeneration();
        }

        addFile({
          type: "file",
          fullPath,
        });

        if (reset) {
          toast.info("Started new design with file as context!");
        } else {
          toast.info("Added file as context!");
        }
      },
      [resetGeneration, addFile, fullPath]
    );

    return (
      <ContextMenu>
        <ContextMenuTrigger>{children}</ContextMenuTrigger>
        <ContextMenuContent className="w-64">
          <ContextMenuSub>
            <ContextMenuSubTrigger>
              <span>
                <MagicWandIcon className="mr-2" />
              </span>
              Workflows
            </ContextMenuSubTrigger>
            <ContextMenuSubContent className="w-64">
              <ContextMenuItem
                onClick={() => onAddToDesignSystem({ reset: true })}
              >
                Add to new design
              </ContextMenuItem>
              <ContextMenuItem
                onClick={() => onAddToDesignSystem({ reset: false })}
                disabled={status === GenerationStatus.Pending}
              >
                Use context for existing workflow
              </ContextMenuItem>
            </ContextMenuSubContent>
          </ContextMenuSub>
        </ContextMenuContent>
      </ContextMenu>
    );
  }
);

interface AssetItemProps {
  element: JsxElement;
  level?: number;
  onExpand?: () => void;
  onSelect?: () => void;
  selected: boolean;
  variants?: JsxElement[];
  variant?: boolean;
  expanded?: boolean;
}

export const AssetItem = React.memo(
  ({ element, onSelect, selected, variants = [] }: AssetItemProps) => {
    const dispatch = useDispatch();
    const elementToUseForImage =
      element.type === JsxElementType.Component && variants?.length
        ? variants[0]
        : element;

    const { imagePath } = useImagePreview(
      elementToUseForImage as unknown as AssetsElement
    );

    const variantCount = variants?.length || 0;

    const handleDragStart = useCallback(
      (e: React.DragEvent) => {
        e.dataTransfer.dropEffect = "move";
        if (!element.codebaseId) return;

        let elementToUse = element;
        if (element.type === JsxElementType.Component && variants?.length) {
          elementToUse = variants[0];
        }

        if (
          elementToUse.type === JsxElementType.StorybookComponent &&
          !elementToUse.storybookSourceStart
        ) {
          toast.error("Could not parse storybook source code");
          return;
        }

        dispatch(setDraggingComponent(elementToUse.type));
        e.dataTransfer.setData("tempo/codebaseId", elementToUse.codebaseId);
        if (elementToUse.isTemplateStory) {
          e.dataTransfer.setData(
            "tempo/isTemplateStory",
            elementToUse.isTemplateStory.toString()
          );
        }
      },
      [element, variants, dispatch]
    );

    const handleClick = useCallback(() => {
      onSelect?.();
    }, [onSelect]);

    return (
      <AssetContextMenu fullPath={element.filename} key={element.key}>
        <BaseCard
          title={element.componentName || "Component"}
          imageUrl={imagePath || undefined}
          count={variantCount > 0 ? variantCount : undefined}
          selected={selected}
          onClick={handleClick}
          onDragStart={handleDragStart}
          className="aspect-square"
          isThumbnail={true}
        />
      </AssetContextMenu>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function to prevent unnecessary re-renders
    return (
      prevProps.selected === nextProps.selected &&
      prevProps.element.key === nextProps.element.key &&
      prevProps.element.componentName === nextProps.element.componentName &&
      prevProps.variants?.length === nextProps.variants?.length
    );
  }
);
